/**
 * Fleet overview: pure summaries of the server-provided fleet list — alerts (fleets in combat),
 * the arrivals timeline, damage and upkeep totals — plus the group helpers and multi-select
 * rules of the list. Nothing here decides game outcomes; all numbers come from the server.
 */
import type { FleetClient, FleetGroupClient } from '@galaxis/shared';
import { isActiveFleet } from './fleet-category.ts';

export interface Arrival { fleetId: string; name: string; systemId: string; at: number }

export interface FleetOverviewData {
  total: number;
  traveling: number;
  working: number;
  idle: number;
  /** Fleets in a battle right now — the alert. */
  inCombat: FleetClient[];
  /** Fleets with damaged ships, and the ship count. */
  damagedFleets: number;
  damagedShips: number;
  /** Idle damaged fleets that could be repaired (idle) — a hint, the server decides. */
  repairable: number;
  /** Upcoming arrivals, soonest first. */
  arrivals: Arrival[];
  upkeepMetalPerHour: number;
}

export function summarizeFleets(fleets: FleetClient[], now: number): FleetOverviewData {
  const active = fleets.filter(isActiveFleet);
  const arrivals: Arrival[] = [];
  const out: FleetOverviewData = {
    total: active.length, traveling: 0, working: 0, idle: 0, inCombat: [],
    damagedFleets: 0, damagedShips: 0, repairable: 0, arrivals, upkeepMetalPerHour: 0,
  };
  for (const f of active) {
    out.upkeepMetalPerHour += f.stats.upkeepMetalPerHour ?? 0;
    if (f.stats.damagedShips > 0) {
      out.damagedFleets++;
      out.damagedShips += f.stats.damagedShips;
      if (f.status === 'idle') out.repairable++;
    }
    switch (f.status) {
      case 'traveling': {
        out.traveling++;
        const dest = f.destinationSystemId;
        const at = f.eta ? new Date(f.eta).getTime() : NaN;
        if (dest && Number.isFinite(at)) arrivals.push({ fleetId: f.id, name: f.name, systemId: dest, at: Math.max(at, now) });
        break;
      }
      case 'surveying':
      case 'constructing': out.working++; break;
      case 'in_combat': out.inCombat.push(f); break;
      default: out.idle++;
    }
  }
  arrivals.sort((a, b) => a.at - b.at || a.name.localeCompare(b.name, 'hu'));
  out.inCombat.sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  return out;
}

// ---- groups ----

export type GroupFilter = 'any' | 'none' | string;

export function groupMatches(f: Pick<FleetClient, 'groupId'>, filter: GroupFilter): boolean {
  if (filter === 'any') return true;
  if (filter === 'none') return !f.groupId;
  return f.groupId === filter;
}

export function groupCounts(fleets: FleetClient[], groups: FleetGroupClient[]): { counts: Map<string, number>; none: number } {
  const counts = new Map(groups.map(g => [g.id, 0]));
  let none = 0;
  for (const f of fleets.filter(isActiveFleet)) {
    if (f.groupId && counts.has(f.groupId)) counts.set(f.groupId, counts.get(f.groupId)! + 1);
    else none++;
  }
  return { counts, none };
}

// ---- multi-select ----

/** Toggles one id; returns a new set. */
export function toggleId(sel: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(sel);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

/** Keeps only ids that still exist (a merged / destroyed fleet leaves the selection). */
export function pruneSelection(sel: ReadonlySet<string>, fleets: FleetClient[]): Set<string> {
  const alive = new Set(fleets.filter(isActiveFleet).map(f => f.id));
  const next = new Set([...sel].filter(id => alive.has(id)));
  return next.size === sel.size ? (sel as Set<string>) : next;
}

/** "Select all visible": if everything visible is selected, clears just those. */
export function toggleAllVisible(sel: ReadonlySet<string>, visibleIds: string[], max: number): Set<string> {
  const allIn = visibleIds.length > 0 && visibleIds.every(id => sel.has(id));
  if (allIn) return new Set([...sel].filter(id => !visibleIds.includes(id)));
  const next = new Set(sel);
  for (const id of visibleIds) { if (next.size >= max) break; next.add(id); }
  return next;
}

/** Bulk results → one summary line. */
export function bulkSummary(results: Array<{ ok: boolean }>): string {
  const ok = results.filter(r => r.ok).length;
  const fail = results.length - ok;
  if (fail === 0) return `${ok} flotta megkapta a parancsot.`;
  if (ok === 0) return `Egyik flotta sem kapta meg a parancsot (${fail} hiba).`;
  return `${ok} flotta megkapta a parancsot, ${fail} nem.`;
}
